'use client';

/**
 * NavBreadcrumb - Icon Navigation + Breadcrumb Trail
 *
 * Unified breadcrumb system:
 * - Mobile icon buttons: Data-driven from NavigationProvider (database groups)
 * - Breadcrumb trail: Route registry for path patterns, params, parent chains
 * - Group metadata (labels, colors, order): From database via NavigationProvider
 *
 * Two-part navigation in header:
 * 1. Icon buttons for main sections (hover reveals full menu)
 * 2. Breadcrumb trail showing current location with sibling dropdowns
 *
 * Example: [🏠][📞][📁][🔍][🛡️] | Dashboard › Reports › Case #abc1...
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useIsStaff, useIsAdmin, useCurrentRoute, useAccessControlState } from '../AppShellContext';
import { breadcrumbTokens, breadcrumbColorSchemes } from './breadcrumb';
import { routeRegistry } from '@/lib/routes/registry';
import { generateBreadcrumbs, substitutePathParams } from '@/lib/routes/breadcrumbs';
import { useNavigationOptional } from '@/lib/navigation/hooks';
import { resolveIcon } from '@/lib/navigation/icons';
import { getColorScheme as getNavColorScheme } from '@/lib/ui/colorSchemes';
import { toneFromColorName, type OptionTone } from '@/components/ui/overlay-recipes';
import type { RouteConfig, BreadcrumbItem, RouteParams } from '@/lib/routes/types';
import type { ResolvedNavGroup, ResolvedNavItem, NavColorScheme } from '@/lib/navigation/types';
import type { LucideIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  MenuOptionContent,
} from '@/components/ui/dropdown-menu';

// ============================================================================
// Types
// ============================================================================

interface NavBreadcrumbProps {
  className?: string;
}

type BreadcrumbColor = 'blue' | 'amber' | 'emerald' | 'cyan' | 'purple' | 'slate';

type BreadcrumbIcon = LucideIcon | string;

interface BreadcrumbWithSiblings {
  id: string;
  label: string;
  href: string;
  current: boolean;
  icon?: BreadcrumbIcon;
  color: BreadcrumbColor;
  siblings: Array<{
    id: string;
    label: string;
    href: string;
    icon?: BreadcrumbIcon;
    active: boolean;
    color: BreadcrumbColor;
    group?: string;
    order?: number;
  }>;
}

// ============================================================================
// Color Schemes (using shared tokens)
// ============================================================================

const colorSchemes: Record<string, typeof breadcrumbColorSchemes.blue> = {
  ...breadcrumbColorSchemes,
  // Extend with slate for settings group
  slate: breadcrumbColorSchemes.purple, // Fallback to purple for breadcrumb display
};

// ============================================================================
// Helper: Map NavColorScheme to BreadcrumbColor
// ============================================================================

function toBreadcrumbColor(colorScheme: NavColorScheme | string | undefined): BreadcrumbColor {
  switch (colorScheme) {
    case 'blue': return 'blue';
    case 'amber': return 'amber';
    case 'emerald': return 'emerald';
    case 'cyan': return 'cyan';
    case 'purple': return 'purple';
    case 'slate': return 'purple'; // Settings uses purple in breadcrumbs
    default: return 'blue';
  }
}

// ============================================================================
// Helper: Get color from route (with DB navigation fallback)
// ============================================================================

function getRouteColor(
  route: RouteConfig,
  navGroups?: ResolvedNavGroup[]
): BreadcrumbColor {
  // Try to find color from database navigation groups
  if (navGroups && navGroups.length > 0) {
    // Check path against navigation items for an exact match
    for (const group of navGroups) {
      for (const item of group.items) {
        if (item.path === route.path || route.path.startsWith(item.path + '/')) {
          return toBreadcrumbColor(item.colorScheme || group.colorScheme);
        }
      }
    }
    // Check by group path patterns
    for (const group of navGroups) {
      for (const item of group.items) {
        if (route.path.startsWith(item.path.split('/').slice(0, 2).join('/'))) {
          return toBreadcrumbColor(group.colorScheme);
        }
      }
    }
  }

  // Fallback: derive from route config
  if (route.nav?.group) {
    switch (route.nav.group) {
      case 'navigation':
        if (route.path.startsWith('/adminpanel') || route.portal === 'admin') return 'purple';
        if (route.path.startsWith('/developer')) return 'cyan';
        if (route.path.includes('search')) return 'cyan';
        if (route.path.includes('report') || route.path.includes('recording')) return 'emerald';
        if (route.path.includes('call') || route.path.includes('dispatch')) return 'amber';
        return 'blue';
      case 'admin':
      case 'analytics':
      case 'settings':
        return 'purple';
      case 'developer':
        return 'cyan';
      default:
        return 'blue';
    }
  }

  if (route.portal === 'admin') return 'purple';
  if (route.portal === 'developer') return 'cyan';
  if (route.path.startsWith('/adminpanel')) return 'purple';

  return 'blue';
}

// ============================================================================
// Helper: Get siblings for a route (routes with same breadcrumbParent)
// ============================================================================

function getSiblings(
  route: RouteConfig,
  currentRouteId: string,
  params: Record<string, string> = {},
  navGroups?: ResolvedNavGroup[]
): BreadcrumbWithSiblings['siblings'] {
  if (!route.breadcrumbParent) {
    const mainNavRoutes = routeRegistry.getNavigableRoutes().filter(r =>
      r.nav &&
      !r.breadcrumbParent &&
      r.nav.positions?.includes('main') &&
      !r.nav.hidden
    );

    return mainNavRoutes.map(r => {
      const label = r.nav?.label ||
        (typeof r.breadcrumbLabel === 'string' ? r.breadcrumbLabel : null) ||
        (typeof r.title === 'string' ? r.title : r.id);

      return {
        id: r.id,
        label,
        href: r.path,
        icon: r.nav?.icon,
        active: r.id === currentRouteId,
        color: getRouteColor(r, navGroups),
        group: r.nav?.group,
        order: r.nav?.order ?? 999,
      };
    });
  }

  const allRoutes = routeRegistry.getAllRoutes();
  const siblings = allRoutes.filter(r => {
    if (r.breadcrumbParent !== route.breadcrumbParent) return false;
    if (r.hideBreadcrumb) return false;
    if (!r.nav && !r.breadcrumbLabel && r.id !== currentRouteId) return false;

    const hasDynamicSegment = r.path.includes('[');
    if (hasDynamicSegment) {
      const pathParams = r.path.match(/\[([^\]]+)\]/g);
      if (pathParams) {
        for (const paramMatch of pathParams) {
          const paramName = paramMatch.replace(/[\[\]\.]/g, '');
          if (!params[paramName]) {
            if (r.id !== currentRouteId) return false;
          }
        }
      }
    }

    return true;
  });

  return siblings.map(r => {
    const label = typeof r.breadcrumbLabel === 'string'
      ? r.breadcrumbLabel
      : r.nav?.label || (typeof r.title === 'string' ? r.title : r.id);

    let href = r.path;
    if (params && Object.keys(params).length > 0) {
      href = substitutePathParams(r.path, params);
    }

    return {
      id: r.id,
      label,
      href,
      icon: r.nav?.icon,
      active: r.id === currentRouteId,
      color: getRouteColor(r, navGroups),
      group: r.nav?.group,
      order: r.nav?.order ?? 999,
    };
  });
}

// ============================================================================
// Hook: Generate breadcrumbs from route registry
// ============================================================================

export function isAdminOnlyRoute(route: RouteConfig): boolean {
  return route.auth === 'admin' || route.auth === 'platform';
}

function maySeeBreadcrumbRoute(route: RouteConfig, isAdmin: boolean, isPlatformAdmin: boolean): boolean {
  if (!isAdminOnlyRoute(route)) return true;
  return route.auth === 'platform' ? isPlatformAdmin : isAdmin;
}

function useBreadcrumbsFromRegistry(
  pathname: string,
  navGroups?: ResolvedNavGroup[]
): BreadcrumbWithSiblings[] {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbWithSiblings[]>([]);
  const isAdmin = useIsAdmin();
  const isPlatformAdmin = useAccessControlState().userContext?.is_platform_admin === true;

  useEffect(() => {
    let isCancelled = false;

    async function buildBreadcrumbs() {
      const route = routeRegistry.getByPath(pathname);

      if (!route || route.hideBreadcrumb) {
        if (!isCancelled) {
          setBreadcrumbs([]);
        }
        return;
      }

      const params = routeRegistry.extractParams(pathname, route.path);

      const routeParams: RouteParams = {
        params,
        searchParams: {},
      };

      const chain = await generateBreadcrumbs(pathname, routeParams);

      if (isCancelled) return;

      if (chain.length === 0) {
        setBreadcrumbs([]);
        return;
      }

      const visibleChain = chain.filter((crumb) => {
        const crumbRoute = routeRegistry.getById(crumb.id);
        return crumb.current || !crumbRoute || maySeeBreadcrumbRoute(crumbRoute, isAdmin, isPlatformAdmin);
      });

      const withSiblings: BreadcrumbWithSiblings[] = visibleChain.map((crumb) => {
        const crumbRoute = routeRegistry.getById(crumb.id);
        const color = crumbRoute ? getRouteColor(crumbRoute, navGroups) : 'blue';
        const allSiblings = crumbRoute ? getSiblings(crumbRoute, crumb.id, params, navGroups) : [];
        const siblings = allSiblings.filter((sibling) => {
          const siblingRoute = routeRegistry.getById(sibling.id);
          return sibling.id === crumb.id || !siblingRoute || maySeeBreadcrumbRoute(siblingRoute, isAdmin, isPlatformAdmin);
        });

        return {
          id: crumb.id,
          label: crumb.label,
          href: crumb.href,
          current: crumb.current,
          icon: crumbRoute?.nav?.icon,
          color,
          siblings,
        };
      });

      setBreadcrumbs(withSiblings);
    }

    buildBreadcrumbs();

    return () => {
      isCancelled = true;
    };
  }, [pathname, navGroups, isAdmin, isPlatformAdmin]);

  return breadcrumbs;
}

// ============================================================================
// Hook: Build mobile navigation categories from DB groups
// ============================================================================

function useNavCategories() {
  const navContext = useNavigationOptional();
  const groups = navContext?.navigation?.byPosition.sidebar ?? [];

  return useMemo(() => {
    if (groups.length === 0) return [];

    // Filter to groups that should appear in mobile/main positions
    // IMPORTANT: resolve icons from iconName strings, NOT from g.icon —
    // g.icon may be a serialized object from localStorage cache (not a callable component)
    return groups
      .filter(g => g.visible && g.positions.some(p => p === 'mobile' || p === 'sidebar'))
      .map(g => ({
        key: g.key,
        label: g.label,
        href: g.items[0]?.path || '/',
        icon: resolveIcon(g.iconName),
        iconName: g.iconName,
        colorScheme: g.colorScheme,
        color: toBreadcrumbColor(g.colorScheme),
        items: g.items.filter(i => !i.hidden && i.positions.some(p => p === 'sidebar')),
        sortOrder: g.sortOrder,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [groups]);
}

// ============================================================================
// Hook: Build group label/order map from DB groups
// ============================================================================

function useGroupMetadata() {
  const navContext = useNavigationOptional();
  const groups = navContext?.navigation?.groups ?? [];

  return useMemo(() => {
    const labels: Record<string, string> = { default: '' };
    const order: Record<string, number> = { default: 99 };

    for (const group of groups) {
      labels[group.key] = group.label;
      order[group.key] = group.sortOrder;
    }

    // Also map route config group names to DB group data
    // Route config uses 'navigation' as a catch-all; map to operations
    labels['navigation'] = labels['operations'] || 'Navigation';
    order['navigation'] = order['operations'] ?? 1;

    return { labels, order };
  }, [groups]);
}

// ============================================================================
// Component
// ============================================================================

export function NavBreadcrumb({ className = '' }: NavBreadcrumbProps) {
  const pathname = usePathname();
  const route = useCurrentRoute();
  const isStaff = useIsStaff();

  // Data-driven navigation
  const navContext = useNavigationOptional();
  const navGroups = navContext?.navigation?.byPosition.sidebar;
  const categories = useNavCategories();
  const groupMeta = useGroupMetadata();

  // Helper to check if a category is active
  const isCategoryActive = (cat: { href: string; key: string }) => {
    if (pathname === cat.href || pathname.startsWith(cat.href + '/')) return true;
    return false;
  };

  // Generate breadcrumbs from route registry (single source of truth for trail)
  const breadcrumbsWithSiblings = useBreadcrumbsFromRegistry(pathname, navGroups);

  // Find current category for color
  const currentCategory = categories.find(cat => isCategoryActive(cat));
  const currentColor = currentCategory?.color || 'blue';

  if (!isStaff) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 min-w-0 ${className}`}>
      {/* NOTE: Hardcoded Home icon removed — sidebar/header navigation is fully
          data-driven from navigation_groups in the database. If a "home" group
          is desired, add it to the DB (see migration 20260328_nav_restructure). */}

      {/* Mobile: Icon Navigation — data-driven from DB groups */}
      <div className="flex md:hidden items-center gap-0.5">
        {categories.map((category) => (
          <NavIconButton
            key={category.key}
            category={category}
            isActive={isCategoryActive(category)}
            currentPath={pathname}
          />
        ))}
      </div>

      {/* Desktop: Breadcrumb Trail with Dropdown Menus */}
      {breadcrumbsWithSiblings.length > 0 && (
        <nav className="hidden md:flex items-center gap-1 min-w-0 flex-1" aria-label="Breadcrumb">
          {breadcrumbsWithSiblings.map((crumb, index) => (
            <BreadcrumbDropdown
              key={crumb.id}
              crumb={crumb}
              isLast={index === breadcrumbsWithSiblings.length - 1}
              color={crumb.color || currentColor}
              groupMeta={groupMeta}
            />
          ))}
        </nav>
      )}
    </div>
  );
}

// ============================================================================
// Hover menus (std-overlays G14): the breadcrumb sibling menu and the mobile category menu are the shared
// DropdownMenu in look="standard" (popup surface, rows, layer owner), not their own fixed z-1000 panels.
// They keep their behaviour: they open on hover (200 ms grace on leave) and from the crumb's own click / Enter,
// a hover never moves focus, and focus goes back to the crumb only when it was inside the menu.
// ============================================================================

const MENU_CLOSE_DELAY_MS = 200;

function useHoverMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // How it opened: a hover must not take focus from wherever it is.
  const openedByHoverRef = useRef(false);
  // Focus was inside the menu (then a close hands it back to the trigger), and the close was an outside press.
  const focusInMenuRef = useRef(false);
  // The hover box (trigger side): a press on it is the trigger's own click, not an outside press.
  const hoverBoxRef = useRef<HTMLDivElement>(null);

  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  useEffect(() => cancelClose, []);

  const setOpen = (open: boolean, byHover = false) => {
    cancelClose();
    if (open && !isOpen) openedByHoverRef.current = byHover;
    if (open) focusInMenuRef.current = false;
    setIsOpen(open);
  };

  return {
    isOpen,
    setOpen,
    hoverBoxRef,
    onMouseEnter: () => setOpen(true, true),
    onMouseLeave: () => {
      cancelClose();
      closeTimeoutRef.current = setTimeout(() => setIsOpen(false), MENU_CLOSE_DELAY_MS);
    },
    contentProps: (returnFocusTo: React.RefObject<HTMLElement | null>) => ({
      onFocus: () => { focusInMenuRef.current = true; },
      onInteractOutside: (event: Event) => {
        const target = event.target as Node | null;
        if (target && hoverBoxRef.current?.contains(target)) {
          // The crumb toggles the menu itself on click, as before.
          event.preventDefault();
          return;
        }
        focusInMenuRef.current = false;
      },
      onCloseAutoFocus: (event: Event) => {
        event.preventDefault();
        if (focusInMenuRef.current) returnFocusTo.current?.focus();
        focusInMenuRef.current = false;
      },
      // Radix moves focus into a menu when it mounts; a hover-opened menu leaves it where it is.
      onOpenAutoFocus: (event: Event) => {
        if (openedByHoverRef.current) event.preventDefault();
      },
    }),
  };
}

// ============================================================================
// BreadcrumbDropdown Component - Desktop breadcrumb with sibling dropdown
// ============================================================================

interface BreadcrumbDropdownProps {
  crumb: BreadcrumbWithSiblings;
  isLast: boolean;
  color: BreadcrumbColor;
  groupMeta: { labels: Record<string, string>; order: Record<string, number> };
}

function BreadcrumbDropdown({ crumb, isLast, color, groupMeta }: BreadcrumbDropdownProps) {
  const menu = useHoverMenu();
  const { isOpen } = menu;
  const crumbRef = useRef<HTMLElement>(null);
  const colors = colorSchemes[color] || colorSchemes.blue;
  const hasSiblings = crumb.siblings.length > 1;

  // Group siblings by nav.group and sort using DB group metadata
  const groupedSiblings = useMemo(() => {
    const groups = new Map<string, typeof crumb.siblings>();
    for (const sibling of crumb.siblings) {
      const groupKey = sibling.group || 'default';
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(sibling);
    }

    for (const items of groups.values()) {
      items.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    }

    // Sort groups using DB group order
    const sortedGroups = Array.from(groups.entries()).sort(([a], [b]) => {
      const orderA = groupMeta.order[a] ?? 99;
      const orderB = groupMeta.order[b] ?? 99;
      return orderA - orderB;
    });

    return sortedGroups;
  }, [crumb.siblings, groupMeta.order]);

  const hasMultipleGroups = groupedSiblings.length > 1;

  return (
    <div className="flex items-center gap-1 min-w-0">
      <DropdownMenu look="standard" modal={false} open={isOpen && hasSiblings} onOpenChange={(open) => menu.setOpen(open)}>
        <div
          ref={menu.hoverBoxRef}
          className="relative min-w-0"
          onMouseEnter={menu.onMouseEnter}
          onMouseLeave={menu.onMouseLeave}
        >
          {/* The menu is placed against this box; the crumb itself opens it (click, Enter, Space) and keeps its
              own link / button behaviour, so the Radix trigger handlers are off. */}
          <DropdownMenuTrigger asChild disabled type={undefined} aria-haspopup={undefined} aria-expanded={undefined}>
            <div className="min-w-0">
            {/* Breadcrumb Text/Link
                The label span uses `truncate` so very long dynamic crumbs (case
                numbers, file names) get an ellipsis instead of overflowing. We
                cap each crumb at ~10rem on small screens / ~14rem from md up so
                short labels like "Training" or "Achievements" never get cut to
                "Tr..." while there is still horizontal room available. */}
            {isLast ? (
              <span
                ref={crumbRef}
                className={`text-sm font-medium ${colors.text} flex items-center gap-1.5 px-2 py-1 rounded-md ${colors.bg} cursor-pointer min-w-0`}
                onClick={() => hasSiblings && menu.setOpen(!isOpen)}
                // WCAG 2.1.1 / 4.1.2: the sibling menu also opens from the keyboard.
                role={hasSiblings ? 'button' : undefined}
                tabIndex={hasSiblings ? 0 : undefined}
                aria-haspopup={hasSiblings ? 'menu' : undefined}
                aria-expanded={hasSiblings ? isOpen : undefined}
                onKeyDown={(e) => {
                  if (hasSiblings && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    menu.setOpen(!isOpen);
                  }
                }}
              >
                {crumb.icon && typeof crumb.icon !== 'string' && <crumb.icon className="w-3.5 h-3.5 flex-shrink-0" />}
                <span className="truncate max-w-[10rem] md:max-w-[14rem]">{crumb.label}</span>
                {hasSiblings && <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
              </span>
            ) : (
              <Link
                ref={crumbRef as React.Ref<HTMLAnchorElement>}
                href={crumb.href}
                className={`text-sm text-white/60 hover:text-white flex items-center gap-1.5 px-2 py-1 rounded-md transition-all min-w-0
                  ${isOpen ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'}
                `}
              >
                {crumb.icon && typeof crumb.icon !== 'string' && <crumb.icon className="w-3.5 h-3.5 flex-shrink-0" />}
                <span className="truncate max-w-[10rem] md:max-w-[14rem]">{crumb.label}</span>
                {hasSiblings && <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
              </Link>
            )}
            </div>
          </DropdownMenuTrigger>
        </div>

        {hasSiblings && (
          <DropdownMenuContent
            align="start"
            sideOffset={8}
            aria-label={`${crumb.label} pages`}
            data-breadcrumb-menu=""
            className="w-56 max-h-[70vh]"
            onMouseEnter={menu.onMouseEnter}
            onMouseLeave={menu.onMouseLeave}
            {...(menu.contentProps(crumbRef) as object)}
          >
            {groupedSiblings.map(([groupKey, siblings], groupIndex) => (
              <DropdownMenuGroup key={groupKey}>
                {/* Group header — labels from DB */}
                {hasMultipleGroups && groupIndex > 0 && <DropdownMenuSeparator />}
                {hasMultipleGroups && groupMeta.labels[groupKey] && (
                  <DropdownMenuLabel>{groupMeta.labels[groupKey]}</DropdownMenuLabel>
                )}
                {siblings.map((sibling) => {
                  const SiblingIcon = typeof sibling.icon !== 'string' ? sibling.icon : undefined;
                  // Each page keeps its section colour: the icon on a tinted tile, the current page in that colour.
                  const tone = breadcrumbTone(sibling.color || color);
                  return (
                    <DropdownMenuItem key={sibling.id} asChild className={MENU_LINK_ROW}>
                      <Link href={sibling.href} aria-current={sibling.active ? 'page' : undefined}>
                        <MenuOptionContent
                          icon={SiblingIcon}
                          iconTone={tone}
                          iconTile
                          labelTone={sibling.active ? tone : undefined}
                          marker={sibling.active}
                        >
                          {sibling.label}
                        </MenuOptionContent>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
            ))}
          </DropdownMenuContent>
        )}
      </DropdownMenu>

      {/* Chevron separator */}
      {!isLast && <ChevronRight className={`${breadcrumbTokens.separator.icon} ${breadcrumbTokens.separator.base} flex-shrink-0`} />}
    </div>
  );
}

/** A section colour as its category tone (slate settings read as purple, as the crumbs draw them). */
const breadcrumbTone = (color: string | undefined): OptionTone => toneFromColorName(color === 'slate' ? 'purple' : color) ?? 'cat-1';

/** A navigation row of a breadcrumb menu: the standard option row, the current page on the selected fill. */
const MENU_LINK_ROW = 'aria-[current=page]:bg-selected';

// ============================================================================
// NavIconButton - Icon button with hover dropdown (mobile)
// Data-driven from DB navigation groups
// ============================================================================

interface NavCategoryData {
  key: string;
  label: string;
  href: string;
  icon: LucideIcon | null;
  iconName: string | null;
  colorScheme: NavColorScheme;
  color: BreadcrumbColor;
  items: ResolvedNavItem[];
  sortOrder: number;
}

interface NavIconButtonProps {
  category: NavCategoryData;
  isActive: boolean;
  currentPath: string;
}

function NavIconButton({ category, isActive, currentPath }: NavIconButtonProps) {
  const menu = useHoverMenu();
  const { isOpen } = menu;
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const colors = colorSchemes[category.color] || colorSchemes.blue;
  const categoryTone = breadcrumbTone(category.color);
  // Resolve icon from string name at render time (cached objects from localStorage aren't components)
  const Icon = resolveIcon(category.iconName);
  // Limit to 6 items for dropdown
  const categoryItems = category.items.slice(0, 6);

  return (
    <DropdownMenu look="standard" modal={false} open={isOpen && categoryItems.length > 0} onOpenChange={(open) => menu.setOpen(open)}>
      <div
        ref={menu.hoverBoxRef}
        className="relative"
        onMouseEnter={menu.onMouseEnter}
        onMouseLeave={menu.onMouseLeave}
      >
        {/* Icon Button: a link to the category; the menu opens on hover. */}
        <DropdownMenuTrigger asChild disabled>
          <Link
            ref={triggerRef}
            href={category.href}
            aria-current={isActive ? 'true' : undefined}
            className={`
              flex items-center justify-center p-2 rounded-lg transition-all duration-200 group
              ${isActive ? colors.bg : 'hover:bg-white/[0.06]'}
            `}
            title={category.label}
          >
            <div className={`
              p-1.5 rounded-md transition-all duration-200
              ${isActive ? colors.bg : 'bg-white/[0.04]'}
              ${isOpen ? `${colors.bg} scale-110` : ''}
            `}>
              {Icon && <Icon className={`w-4 h-4 ${isActive || isOpen ? colors.text : 'text-white/60'}`} />}
            </div>
          </Link>
        </DropdownMenuTrigger>
      </div>

      {categoryItems.length > 0 && (
        <DropdownMenuContent
          align="start"
          sideOffset={8}
          aria-label={category.label}
          data-category-menu=""
          className="w-64"
          onMouseEnter={menu.onMouseEnter}
          onMouseLeave={menu.onMouseLeave}
          {...(menu.contentProps(triggerRef) as object)}
        >
          {/* Category Header */}
          <DropdownMenuLabel tone={categoryTone}>{category.label}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* Menu Items — from DB navigation items */}
          {categoryItems.map((item) => {
            const ItemIcon = resolveIcon(item.iconName);
            const itemPath = item.path.split('?')[0];
            const isItemActive = currentPath === itemPath || currentPath.startsWith(itemPath + '/');

            return (
              <DropdownMenuItem key={item.key} asChild className={`${MENU_LINK_ROW}${item.description ? ' py-1.5' : ''}`}>
                <Link href={item.path} aria-current={isItemActive ? 'page' : undefined}>
                  <MenuOptionContent
                    icon={ItemIcon}
                    iconTone={categoryTone}
                    iconTile
                    description={item.description || undefined}
                    labelTone={isItemActive ? categoryTone : undefined}
                    marker={isItemActive}
                  >
                    {item.label}
                  </MenuOptionContent>
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
}

// ============================================================================
// Export
// ============================================================================

export default NavBreadcrumb;
